<template>
  <fin-data-loader :resolver="portfolios" :data-adapter="modifyModel">
    <template #content>
      <!-- this mask is called for several purposes: add to portfolio, buy and sell -->
      <div style="background-color: #fff; min-height: calc(100vh - 56px - 54px);">
        <div v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
             v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
             class="pt-6 pt-sm-12">

          <v-container class="pa-0">
            <!--Set Title Text-->
            <v-row class="pb-3">
              <v-col cols="12">
                <h1 v-if="action==='buy'" class="addToPortfolio__title">Wertpapier von {{ model[0].name }} kaufen</h1>
                <h1 v-else-if="action==='sell'" class="addToPortfolio__title">Wertpapier von {{ model[0].name }}
                  verkaufen</h1>
                <h1 v-else class="addToPortfolio__title">{{ title }}</h1>
              </v-col>
            </v-row>

            <!--Choose Portfolio-->
            <v-row v-if="!portfolioId" class="mt-6">
              <v-col v-if="action===''" cols="12" lg="4" md="5" sm="10" xl="3">
                <div class="font-weight-bold choose-label mb-2">Portfolio wählen</div>

                <fin-select-box v-model="selectedPortfolio"
                                :items="activePortfolioList"
                                class="mb-2 mb-sm-8"
                                new-list
                                newListLabel="Neues Portfolio anlegen"
                                return-as-object
                                selectBoxItemTitle="name"
                                selectBoxItemValue="id"
                                style="max-width: 350px;"
                                @input="(val) => portfolioSelectionChanged(val)"
                >
                  <template #icon v-if="!checkIfPortfolioCreationAllowed()">
                    <fin-subscription-lock color="fin-color-primary-dark"/>
                  </template>
                </fin-select-box>
              </v-col>
            </v-row>
          </v-container>
        </div>

        <div v-if="selectedPortfolio"
             v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
             v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
             :style="{ backgroundColor: $finColors.paleGrey }">

          <v-container>
            <v-form v-model="valid">
              <!--Show all Instruments (Companies)-->
              <v-row v-for="(instrument, index) in model"
                     :key="instrument.id"
                     justify="end"
                     class="share">
                <v-col cols="12">
                  <p class="instrument-label mb-0 d-inline">
                    {{ instrument.name }}
                  </p>
                  <v-btn @click="removeItem(instrument)" icon style="float: right">
                    <v-icon :color="$finColors.coolGrey">icon-delete</v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="2" class="py-0">
                  <div v-style:minHeight="$vuetify.breakpoint.mdAndUp ? '50px' : ''">
                    <div class="font-weight-bold mr-2">{{ action === 'sell' ? 'Verkaufspreis' : 'Kaufpreis' }}</div>
                    <div class="footnote mb-1" v-if="instrument.latestPrice">
                      Börse {{ instrument.latestPrice.exchangeName }}; {{ instrument.latestPrice.timestamp | datetime }}
                    </div>
                  </div>
                  <fin-currency-field v-if="instrument.latestPrice"
                                      v-model="instrument.latestPrice.price"
                                      class="input-field__white"
                                      :error-messages="instrument.latestPrice.price === 0 ? 'Bitte gib einen Betrag >0 ein' : ''"
                                      required
                                      @blur="validateTransaction"/>
                </v-col>
                <v-col cols="12" sm="6" md="2" class="py-0">
                  <div v-style:minHeight="$vuetify.breakpoint.mdAndUp ? '50px' : ''">
                    <div class="font-weight-bold mr-2">Stückzahl</div>
                    <div class="footnote">
                      {{ getCountFromPortfolio(instrument) }} Stk bereits im Portfolio vorhanden.
                    </div>
                  </div>
                  <v-text-field v-model.number="instrument.count"
                                :rules="[...rules.countField,
                                ... action === 'sell' ? [(v) => v <= getCountFromPortfolio(instrument) || 'Bitte eine Zahl <= ' + getCountFromPortfolio(instrument) + ' eingeben'] : []]"
                                filled
                                autofocus
                                type="number"
                                @blur="validateTransaction"
                                class="input-field__white"
                  />

                </v-col>
                <v-col cols="12" sm="6" md="2" class="py-0">
                  <div v-style:minHeight="$vuetify.breakpoint.mdAndUp ? '50px' : ''" class="font-weight-bold">Gebühren
                  </div>
                  <fin-currency-field v-model="instrument.fees"
                                      class="fin-currency-input input-field__white"
                                      @blur="validateTransaction"
                                      required
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" class="py-0">
                  <div v-style:minHeight="$vuetify.breakpoint.mdAndUp ? '50px' : ''" class="font-weight-bold mr-2">
                    Datum
                  </div>
                  <fin-date-picker v-model="instrument.date"
                                   :min-date="minDate(instrument)"
                                   :max-date="new Date()"
                                   time-picker
                                   @input="validateTransaction"
                                   class="input-field__white"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="2" class="py-0" align="right">
                  <p v-style:paddingTop="$vuetify.breakpoint.mdAndUp ? '50px' : ''">
                    <span class="instrument-costs d-flex justify-end">
                      {{ sumRow(instrument) | finCurrency }}
                    </span>
                  </p>
                </v-col>
                <v-col v-if="model.length>1"
                       cols="12">
                  <v-divider/>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </div>

        <div v-if="selectedPortfolio"
             v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
             v-style:paddingRight="$valueByBreakpoint($finPadding.right)">

          <v-container>
            <!--Show Value-->
            <v-row>
              <v-col align="right" cols="12" sm="6" offset-md="6" md="3">
                <div>
                  <fin-color-number v-if="hasEnoughCash"
                                    :number-format="$options.filters.finCurrency"
                                    :value="sumAll"
                                    class="sum"/>
                  <span v-else class="sum" :style="{color: $finColors.accentRed}">{{ sumAll | currency }}</span>
                </div>
                <div class="footnote">
                  Summe gesamt
                </div>
              </v-col>
              <v-col align="right" cols="12" sm="6" md="3">
                <div v-if="action === 'sell'">
                  <div :style="`color: ${cashAfterTransaction < 0 ? $finColors.accentRed: $finColors.accentGreen}; `"
                       class="sum">
                    <span>{{ cashAfterTransaction  | finCurrency }}</span>
                  </div>
                  <div class="footnote">
                    Cashbestand nach Verkauf
                  </div>
                </div>
                <div v-else>
                  <div :style="`color: ${cashAfterTransaction < 0 ? $finColors.accentRed: $finColors.accentGreen}; `"
                       class="sum">
                    <span>{{ cashAfterTransaction |  finCurrency }}</span>
                  </div>
                  <div class="footnote mb-2">
                    Cashbestand nach Kauf
                  </div>
                </div>
              </v-col>
              <v-col align="right" v-if="!hasEnoughCash && action !== 'sell'">
                <div>Zum angegebenen Kaufzeitpunkt steht dir nicht genügend Cash zur Verfügung.</div>
                <v-btn :disabled="!isStatusOK"
                       @click="addCash"
                       :loading="isLoading"
                       :color="$finColors.primaryDark"
                       class="px-0" text
                >
                  Fehlenden Betrag einzahlen
                </v-btn>
              </v-col>
              <v-col align="right" v-if="!transactionIsValid && action === 'sell'">
                <div>Gewählte Anzahl zu hoch. Du hast zum gewählten Zeitpunkt nur {{ countAtTime }} Stück
                  {{ model[0].name }}.
                </div>
              </v-col>
            </v-row>

            <!--Buttons (Abbrechen & Übernehmen)-->
            <v-row>
              <v-col v-if="!isStatusOK" cols="12">
                <fin-error-display
                  :error-msg="getPortfolioStatusReadableMessage(selectedPortfolio.status.calculationStatus)"
                  display-html/>
              </v-col>
              <v-col align="right"
                     cols="12">
                <v-btn :color="$finColors.primaryDark"
                       class="mr-5"
                       large
                       outlined
                       @click="cancelButtonEvent">
                  Abbrechen
                </v-btn>
                <v-btn v-if="action==='buy'"
                       :color="$finColors.primaryDark"
                       :loading="isLoading"
                       :disabled="disableConfirm"
                       class="font-weight-bold"
                       dark
                       large
                       @click="buyInstruments">
                  Übernehmen
                </v-btn>
                <v-btn v-else-if="action==='sell'"
                       :color="$finColors.primaryDark"
                       :loading="isLoading"
                       :disabled="disableConfirm"
                       class="font-weight-bold"
                       dark
                       large
                       @click="sellInstruments">
                  Übernehmen
                </v-btn>
                <v-btn v-else
                       :color="$finColors.primaryDark"
                       :loading="isLoading"
                       :disabled="disableConfirm"
                       class="font-weight-bold"
                       dark
                       large
                       @click="buyInstruments">
                  Übernehmen
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </div>

      <!-- Add new Portfolio Dialog -->
      <fin-create-portfolio-dialog v-model="showDialogCreatePortfolio"
                                   :loading="isLoading"
                                   @confirm="createPortfolio"/>

    </template>
  </fin-data-loader>
</template>

<script>
import FinDataLoader from "@/components/common/DataLoader"
import {finPortfolioAPI} from "@/js/finAPI"
import FinSelectBox from "@/components/common/SelectBox"
import FinColorNumber from "@/components/common/ColorNumber"
import FinErrorDisplay from "@/components/common/ErrorDisplay"
import FinCreatePortfolioDialog from "@/components/common/portfolio/CreatePortfolioDialog"
import {mapActions, mapGetters} from "vuex"
import FinCurrencyField from "@/components/common/CurrencyField"
import FinDatePicker from "@/components/common/DatePicker"
import {onUpdatePortfolioStatus} from "@/js/finSubscriptions"
import SubscriptionHandler from "@/mixins/SubscriptionHandler"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import {EventBus} from "@/plugins/fincraft"
import PriceModel from "@/mixins/PriceModel"
import FinSubscriptionLock from "@/components/common/subscription/lock"
import {API, graphqlOperation} from "aws-amplify"

export default {
  name: "FinAddToPortfolioDisplay",
  components: {
    FinSubscriptionLock,
    FinDatePicker,
    FinCurrencyField,
    FinCreatePortfolioDialog,
    FinErrorDisplay,
    FinColorNumber,
    FinSelectBox,
    FinDataLoader
  },
  mixins: [SubscriptionHandler, PriceModel],
  props: {
    /** Value, Promise or Function (that returns a Promise or value), the portfolio result list is used if no portfolioId is set */
    portfolios: {},
    /** The user id to set the portfolio changes for */
    userId: {
      type: String,
      required: true
    },
    /** The instruments to buy | sell */
    instruments: {
      type: Array
    },
    /** The title to display if no action is set */
    title: {
      type: String
    },
    /** Possible actions: buy | sell */
    action: {
      type: String,
      default: ''
    },
    /** The id of the portfolio which is affected by the buy | sell action. When calling this page from a portfolio page, the portfolioId is already known. */
    portfolioId: {
      type: String,
      default: undefined
    },
    /** The navigation after successful buy | sell */
    to: {
      type: Object,
      default: undefined
    },
    /** If set, the cancel click only triggers an emit of addToPortfolioCancel without any redirect */
    cancelNoRedirect: {
      type: Boolean,
      default: false
    },
    /** If set, the submit click only triggers an emit of addToPortfolioDone without any redirect */
    disableDefaultSubmitNavigation: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isLoading: false,
      nbOfPortfolios: null,
      activePortfolioList: [],
      selectedPortfolio: undefined,
      model: [],
      showDialogCreatePortfolio: false,
      portfolioNameInput: null,
      portfolioCapitalInput: null,
      illustrationHeight: 140,
      greetingTopMarigin: -150,
      prevRoute: null,
      valid: false,
      transactionIsValid: false,
      cashDeficit: 0,
      causes: [],
      cashAfterTransaction: 0,
      countAtTime: 0,
      rules: {
        textField: [value => !!value || 'Bitte Portfolioname eingeben'],
        countField: [
          v => (!!v || v === 0) || 'Bitte eine Zahl eingeben',
          v => {
            // Workaround to exclude exponential notation
            v = v.toString()
            if (v.includes("e")) {
              return 'Bitte eine Zahl eingeben'
            } else {
              return true
            }
          },
          v => !Number.isNaN(v) || 'Bitte eine Zahl eingeben',
          v => v > 0 || 'Bitte eine Zahl >0 eingeben'
        ]
      },
      queryList: `{
        ${this.instruments.map(i =>
        `${i.id} : ${i.assetClass === 'SHARE' ? 'getShare' : 'getEtf'}(id:"${i.id}") {
             firstPriceDate
          }`
      )}
      }`
    }
  },
  computed: {
    ...mapGetters('auth', ['showOnBoarding', 'userOnboardingData']),
    cash() {
      return this.selectedPortfolio?.cash
    },
    hasEnoughCash() {
      if (this.action === 'sell') {
        return true
      } else {
        return !(this.causes?.reduce((pv, cv) => pv + cv.cashDeficit, 0) > 0) &&
          this.cashAfterTransaction >= 0 && this.cashDeficit === 0
      }
    },
    sumAll() {
      let result = 0
      let intermediateResult = 0
      if (this.model) {
        this.model.forEach(instrument => {
          intermediateResult = instrument.count >= 1 ? (instrument.latestPrice.price * instrument.count) + instrument.fees : 0
          result = result + intermediateResult
        })
      }
      return result
    },
    toModified() {
      if (this.portfolioId) {
        let mod = JSON.parse(JSON.stringify(this.to))
        mod.params.tab = this.instruments[0].assetClass
        return mod
      } else {
        return this.to
      }
    },
    isStatusOK() {
      return !this.selectedPortfolio || !this.selectedPortfolio.status || !this.selectedPortfolio.status.calculationStatus ||
        this.selectedPortfolio.status.calculationStatus === 'OK'
    },
    disableConfirm() {
      return !this.selectedPortfolio || !this.isStatusOK || !this.hasEnoughCash || this.instrumentCountZero || !this.valid || !this.transactionIsValid
    },
    instrumentCountZero() {
      if (this.model.length !== 0) {
        return this.model.map(instrument => instrument.count).reduce((prev, next) => prev + next) === 0
      } else {
        return true
      }
    },
  },
  methods: {
    ...mapActions('auth', ['updateOnboarding']),
    cancelButtonEvent() {
      this.cancelNoRedirect ? this.$emit('addToPortfolioCancel') : this.to ? this.$router.push(this.toModified) : this.$router.go(-1)
    },
    removeItem(item) {
      this.model = this.model.filter(instrument => instrument.id !== item.id)
      if (this.model.length === 0) {
        this.cancelButtonEvent()
      }
      this.validateTransaction()
    },
    sumRow(instrument) {
      return instrument.count >= 1 ? instrument.count * instrument.latestPrice.price + instrument.fees : 0
    },
    async addCash() {
      this.isLoading = true
      try {
        await this.validateTransaction()

        for (const cause of this.causes) {
          await finPortfolioAPI.cashPayIn(this.selectedPortfolio.id, cause.cashDeficit, cause.actionTimestamp)
        }
        // TODO temporary fix to show the adjusted cash since portfolio doesnt reload
        this.selectedPortfolio.cash += this.cashDeficit
        await this.validateTransaction()
        this.$toasted.global.finToast({
          message: 'Der verfügbare Betrag wurde erhöht.',
          type: 'success',
        })
      } catch (e) {
        console.error("Failed to update cash and transaction", e)
        this.$toasted.global.finToast({
          message: 'Der Betrag konnte nicht hinzugefügt werden.',
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    async reloadPortfolios(portfolioId) {
      const portfolios = await this.portfolios()
      this.nbOfPortfolios = portfolios.length
      this.activePortfolioList = portfolios.filter(portfolio => portfolio.inactive !== true)
      this.selectedPortfolio = this.activePortfolioList[this.activePortfolioList.findIndex(p => p.id === portfolioId)]
    },

    resetToCurrentDate() {
      const now = new Date()
      this.model.forEach(instrument => instrument.date = now)
    },

    async validateTransaction() {
      let modelCopy = this.model.filter(m => m.count > 0)

      const transactionType = this.action === 'sell' ? 'SELL' : 'BUY'

      const {
        isValid,
        cash,
        cashDeficit,
        causes
      } = await finPortfolioAPI.validateBuySellTransactions(this.selectedPortfolio.id, modelCopy, transactionType)
      this.transactionIsValid = isValid
      // The cash returned by the validation already includes potential cashPayIns that have to be made in case of negative cash (cashDeficit)
      // thats why we have to use the currentPortfolioCash - cashDeficit for that
      if (transactionType === 'BUY') {
        this.cashAfterTransaction = this.cash - this.sumAll
      } else {
        this.cashAfterTransaction = cash
      }

      this.cashDeficit = cashDeficit
      this.causes = causes
      if (!isValid && transactionType === 'SELL') {
        let {message} = causes[causes.length - 1]
        if (message === "Die erste Positionstransaktion ist eine Verkaufstransaktion!") {
          this.countAtTime = 0
        } else {
          const regEx = /\d+/g
          let result = message.match(regEx)
          let missingCount = result ? result[0] : 0
          this.countAtTime = modelCopy[0].count - missingCount
        }
      }
      return isValid
    },

    getMinimalTimestamp() {
      let minTimestamp = null
      this.model.forEach(i => {
        const ts = i.date.toISOString()
        if (!minTimestamp) {
          minTimestamp = ts
        } else {
          minTimestamp = minTimestamp < ts ? minTimestamp : ts
        }
      })
      return minTimestamp
    },
    async modifyModel(val) {
      this.nbOfPortfolios = val.length
      this.activePortfolioList = val.filter(portfolio => portfolio.inactive !== true)
      this.selectedPortfolio = val[0]
      const {data} = await API.graphql(graphqlOperation(this.queryList))

      // TODO needs some refactoring later on
      // the api reads the count of the instruments instead of the shareEntries/etfEntries array
      this.model = this.instruments.map((i) => {
        return {
          ...i,
          count: 1,
          date: new Date(),
          fees: 0,
          firstPriceDate: data[i.id]?.firstPriceDate,
        }
      })
      await this.validateTransaction()
      return val
    },

    minDate(instrument) {
      if (this.action === 'sell') {
        return this.firstBuyDate(instrument)
      } else if (instrument.firstPriceDate) {
        return new Date(instrument.firstPriceDate)
      } else {
        return undefined
      }
    },
    async buyInstruments() {
      this.isLoading = true
      try {
        const isValid = await this.validateTransaction()
        if (!isValid) {
          return
        }
        // TODO needs some refactoring later on
        // the api reads the count of the instruments instead of the shareEntries/etfEntries array
        this.model = this.model.filter(m => m.count > 0)


        await finPortfolioAPI.transactionBuyInstruments(this.selectedPortfolio.id, this.model)

        let toastMsg = this.action === 'buy' ?
          `Das Wertpapier %wpName% wurde erfolgreich gekauft.` :
          `Das Wertpapier %wpName% wurde erfolgreich ins Portfolio %portfolioName% übernommen.`
        if (this.model.length > 1) {
          toastMsg = this.action === 'buy' ?
            'Die Wertpapiere wurden erfolgreich gekauft.' :
            `Die Wertpapiere wurden erfolgreich ins Portfolio %portfolioName% übernommen.`
        }
        this.$toasted.global.finToast({
          message: toastMsg,
          placeholders: [{name: 'portfolioName', content: this.selectedPortfolio.name}, {
            name: 'wpName',
            content: this.model[0].name
          }],
          type: 'success',
          options: {
            action: this.to === undefined || this.to.params.id !== this.selectedPortfolio.id ? {
              text: 'Zum Portfolio',
              push: {name: 'portfolioDetail', params: {id: this.selectedPortfolio.id}}
            } : {}
          }
        })
      } catch (e) {
        console.error("Failed to buy instruments", e, e.errors[0].message, typeof e.errors[0].message, this.model, this.selectedPortfolio, this.action)
        let msg = this.action === 'buy' ?
          `Das Wertpapier ${this.model[0].name} konnte nicht erfolgreich gekauft werden.` :
          `Das Wertpapiere ${this.model[0].name} konnte nicht erfolgreich ins Portfolio ${this.selectedPortfolio.name} übernommen werden.`
        if (this.model.length > 1) {
          msg = this.action === 'buy' ?
            'Die Wertpapiere konnten nicht erfolgreich gekauft werden.' :
            `Die Wertpapiere konnten nicht erfolgreich ins Portfolio ${this.selectedPortfolio.name} übernommen werden.`
        }
        let jsonErrorInfos = JSON.parse(e.errors[0].message)
        if (jsonErrorInfos.type === "CashExceedsException") {
          msg += ' Dein Cashbestand reicht nicht, um den Wertpapierkauf zu decken. Es fehlen ' +
            this.$options.filters.finCurrency(jsonErrorInfos.cashDeficit)
            + '. Erhöhe den Cashbestand oder passe die Transaktion an.'
        }
        if (jsonErrorInfos.type === "QuotaExceededException") {
          msg += ' Die maximale Anzahl von 150 Portfoliopositionen wurde überschritten.'
        }
        this.$toasted.global.finToast({
          message: msg,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }

      if (this.to) {
        this.$emit("addToPortfolioDone")
        await this.$router.push(this.toModified)
      } else if (this.disableDefaultSubmitNavigation) {
        this.$emit("addToPortfolioDone")
      } else {
        // TODO only make sense when there is no more than one instrument
        let assetClassId = this.model[0].assetClass && this.model[0].assetClass.includes('ETF') ?
          this.model[0].assetClass : 'SHARE'
        await this.$router.push({
          name: assetClassId === 'SHARE' ? 'companyDetail' : 'etfDetail',
          params: {id: this.model[0].id}
        })
      }
    },
    async sellInstruments() {
      this.isLoading = true
      try {
        const isValid = await this.validateTransaction()
        if (!isValid) {
          return
        }
        // TODO needs some refactoring later on
        // the api reads the count of the instruments instead of the shareEntries/etfEntries array
        this.model = this.model.filter(m => m.count > 0)
        await finPortfolioAPI.transactionSellInstruments(this.selectedPortfolio.id, this.model)

        let toastMsg = `Das Wertpapier ${this.model[0].name} wurde erfolgreich verkauft.`
        if (this.model.length > 1) {
          toastMsg = 'Die Wertpapiere wurden erfolgreich verkauft.'
        }
        this.$toasted.global.finToast({
          message: toastMsg,
          type: 'success',
          options: {
            action: this.to.params.id !== this.selectedPortfolio.id ? {
              text: 'Zum Portfolio',
              push: {name: 'portfolioDetail', params: {id: this.selectedPortfolio.id}}
            } : {}
          }
        })
      } catch (e) {
        console.error("sellInstruments: an error occured", e)

        let toastMsgError = `Das Wertpapier ${this.model[0].name} konnte nicht erfolgreich verkauft werden.`
        if (this.model.length > 1) {
          toastMsgError = 'Die Wertpapiere konnten nicht erfolgreich verkauft werden.'
        }
        this.$toasted.global.finToast({
          message: toastMsgError,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }

      if (this.to) {
        await this.$router.push(this.to)
      } else if (this.disableDefaultSubmitNavigation) {
        this.$emit("addToPortfolioDone")
      } else {
        // TODO only make sense when there are instruments from no more than one asset class
        let assetClassId = this.model[0].assetClass && this.model[0].assetClass.includes('ETF') ?
          this.model[0].assetClass : 'SHARE'
        await this.$router.push({
          name: assetClassId === 'SHARE' ? 'companyDetail' : 'etfDetails',
          params: {id: this.model[0].id}
        })
      }
    },
    createPortfolio({name, capital}) {
      this.isLoading = true
      finPortfolioAPI.createPortfolio(name, capital, 0)
        .then(created => {
          const id = created.id

          // if the user hasn't finished the onboarding = cancelled + skipped process at fasttrack
          // => set initialOnboardingFinished (at next login the user isn't presented the fasttrack ui => Dashboard)
          if (this.showOnBoarding) {
            this.userOnboardingData.initialOnboardingFinished = true
            const jsonVal = JSON.stringify(this.userOnboardingData) || '{}'
            this.updateOnboarding(jsonVal).catch(error => {
              console.error("Error setting initialOnboardingFinished after creating first portfolio", error)
            })
          }

          this.activePortfolioList = [created, ...this.activePortfolioList]
          this.$toasted.global.finToast({
            message: `Dein Portfolio %name% wurde erstellt`,
            placeholders: [{name: 'name', content: name}],
            type: 'success',
            options: {
              action: {
                text: 'Zum Portfolio',
                push: {name: 'portfolioDetail', params: {id: id}}
              }
            }
          })
          this.showDialogCreatePortfolio = false
          this.selectedPortfolio = created
          this.resetToCurrentDate()
        }).catch(error => {
        console.error('Failed to create portfolio ' + name, error)
        this.$toasted.global.finToast({
          message: 'Dein Portfolio %portfolioName% konnte nicht erstellt werden.',
          placeholders: [{name: "portfolioName", content: name}],
          type: 'error'
        })
      }).finally(() => {
        // FINDEV-2513
        setTimeout(() => {
          this.isLoading = false
        }, 500)
      })
    },
    checkIfPortfolioCreationAllowed() {
      return this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.PORTFOLIO.id) &&
        this.nbOfPortfolios < this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.PORTFOLIO.id)
    },
    portfolioSelectionChanged(val) {
      if (val === 'Neues Portfolio anlegen') {
        if (this.checkIfPortfolioCreationAllowed()) {
          this.showDialogCreatePortfolio = true
        } else {
          let newCount = this.activePortfolioList.length
          EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.PORTFOLIO.id, PRICEMODEL_FEATURE_RESTRICTIONS.QUANTITY, {itemAmount: ++newCount})
        }

        //this.selectedPortfolio = null
      } else {
        this.validateTransaction()
      }
    },
    getCountFromPortfolio(instrument) {
      const elementsSelector = instrument.assetClass === 'SHARE' ? 'shareEntries' : 'etfEntries'
      const elementList = this.selectedPortfolio ? this.selectedPortfolio[elementsSelector]?.items : undefined
      const element = elementList?.find(el => el.instrument.id === instrument.id)

      if (element) {
        return element.count
      }
      return 0
    },
    getPortfolioStatusReadableMessage(status) {
      if (status === 'FAILED') {
        return `Bei der Berechnung deines Portfolios ${this.selectedPortfolio.name} ist ein Fehler aufgetreten. Solange das Problem besteht, kannst Du keine Wertpapiere kaufen/verkaufen. Bitte kontaktiere den Support unter <a href="mailto:support@fincraft.de">support@fincraft.de</a>`
      } else if (status === 'OK') {
        return 'Die Daten zu deinem Portfolio sind aktuell.'
      } else {
        return `Dein Portfolio ${this.selectedPortfolio.name} wird gerade neu berechnet. Während der Berechnung kannst Du keine Wertpapiere kaufen/verkaufen.`
      }
    },
    firstBuyDate(instrument) {
      const elementsSelector = instrument.assetClass === 'SHARE' ? 'shareEntries' : 'etfEntries'
      const elementList = this.selectedPortfolio[elementsSelector].items
      const {dateAdded} = elementList.find(el => el.instrument.id === instrument.id)

      return new Date(dateAdded)
    },
    unsubscribePortfolioMutations() {
      this.unsubscribeChanges()
    },
    subscribePortfolioMutations() {
      // subscribe to portfolio status changes
      this.subscribeToChanges(
        this.selectedPortfolio,
        onUpdatePortfolioStatus,
        (pf) => {
          return {owner: this.userId, id: pf.id}
        },
        this.handlePortfolioStatusSubscription
      )
    },
    handlePortfolioStatusSubscription(newData, item) {
      // update banner information and displayed data
      this.selectedPortfolio.status = newData.data.onUpdatePortfolio.status

      if (this.selectedPortfolio.status.calculationStatus === undefined || this.selectedPortfolio.status.calculationStatus === 'OK') {
        this.reloadPortfolios(this.selectedPortfolio.id)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.addToPortfolio__title {
  font-size: 28px;
  font-weight: 500;

  @media only screen and (max-width: 960px) {
    font-size: 24px;
  }
}

.instrument-costs {
  font-size: 1.4em;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
  text-align: right;
}

.share {
  background-color: $fin-color-pale-grey;
}

.sum {
  font-size: 2.2em;
  font-weight: bold;
}

.footnote {
  font-size: 0.8em;
  color: $fin-color-cool-grey !important;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  font-family: $body-font-family;
}

.choose-label {
  font-size: 1.1em;
}

.instrument-label {
  font-family: $body-font-family !important;
  font-weight: bold;
  font-size: 1.7em;
  margin-bottom: 12px;

  @media only screen and (max-width: 600px) {
    font-size: 20px;
  }
}

.remaining-capital-text {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

/* arrow in dropdown to select portfolio */
::v-deep .v-select__slot i {
  color: $fin-color-primary-dark !important;
}

::v-deep .theme--light.v-text-field--outlined fieldset {
  border-color: #cfd1d3 !important;
  border-width: 1px !important;
}

::v-deep .v-slider__track-container {
  height: 3px;
}

::v-deep .v-slider__thumb:before {
  -webkit-transform: scale(0.8) !important;
  transform: scale(0.8) !important;
  color: #ffffff;
  box-shadow: $fin-color-shadow;
  border: 1px solid $fin-color-light-sky-blue;
}

::v-deep .v-slider__thumb-container .v-slider__thumb:before {
  opacity: 1;
  color: $fin-color-primary-light;
}

::v-deep .v-slider__thumb-container:hover .v-slider__thumb:before {
  opacity: 1;
  color: $fin-color-primary-light;
}

::v-deep .v-slider__thumb:after {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: $fin-color-primary-dark;
  opacity: 1;
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
}


.input-field__white {

  margin-top: 4px;

  ::v-deep {

    input {
      text-align: end !important;
    }

    .v-input__slot {
      padding: 10px;
      box-shadow: $fin-color-shadow;
      border: solid 1px #cfd1d3;
      background-color: #ffffff !important;
    }

    .v-text-field__suffix {
      padding-left: 0;
      color: black;
    }

  }
}
</style>
